import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import "./BadgeMultiSelect.scss";

function normalizeOptions(options) {
  return (options || []).map((option) =>
    typeof option === "string" ? { value: option, label: option } : option,
  );
}

function normalizeValue(value) {
  if (Array.isArray(value)) return value.filter((item) => item && item !== "All");
  if (value && value !== "All") return [value];
  return [];
}

const BadgeMultiSelect = ({
  options = [],
  value = [],
  onChange,
  addLabel = "+ Add",
  emptyLabel = "All",
  popoverTitle = "Add item",
  popoverSubtitle = "Select one or more",
  disabled = false,
  showSearch = false,
  showSelectAll = false,
  selectAllLabel = "Select all",
  clearLabel = "Clear",
  compactSummary = false,
  inlinePopover = false,
  renderOption = null,
}) => {
  const wrapRef = useRef(null);
  const popoverRef = useRef(null);
  const rootRef = useRef(null);
  const normalizedOptions = useMemo(() => normalizeOptions(options), [options]);
  const selectedValues = useMemo(() => normalizeValue(value), [value]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [position, setPosition] = useState({
    top: 0,
    bottom: null,
    left: 0,
    width: 248,
    listMaxHeight: 220,
    openUpward: false,
  });

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (event) => {
      if (wrapRef.current?.contains(event.target)) return;
      if (popoverRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  useEffect(() => {
    if (!open) setSearch("");
  }, [open]);

  useEffect(() => {
    if (!open || !showSearch) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const input = popoverRef.current?.querySelector(".badge-multi-select__search");
      input?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [open, showSearch]);

  const updateFixedPosition = useCallback(() => {
    const anchor = rootRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const EDGE_GAP = 6;
    const PREFERRED_LIST_HEIGHT = inlinePopover ? 180 : 240;
    const MIN_LIST_HEIGHT = 72;
    const viewportPadding = 12;
    const chromeHeight =
      inlinePopover
        ? (showSearch ? 46 : 0) + (showSelectAll ? 34 : 0)
        : showSearch
          ? 96
          : 54 + (showSelectAll ? 34 : 0);
    const width = Math.max(rect.width, inlinePopover ? rect.width : 200);
    const left = Math.min(
      Math.max(viewportPadding, rect.left),
      window.innerWidth - width - viewportPadding,
    );

    const spaceBelow = window.innerHeight - rect.bottom - EDGE_GAP;
    const spaceAbove = rect.top - EDGE_GAP;

    let openUpward = false;
    let listMaxHeight = PREFERRED_LIST_HEIGHT;

    if (inlinePopover) {
      openUpward = spaceBelow < 140 && spaceAbove > spaceBelow;
      const usableSpace = openUpward ? spaceAbove : spaceBelow;
      listMaxHeight = Math.max(
        MIN_LIST_HEIGHT,
        Math.min(PREFERRED_LIST_HEIGHT, usableSpace - chromeHeight - viewportPadding),
      );
    } else {
      openUpward = spaceBelow < 180 && spaceAbove > spaceBelow;
      const usableSpace = openUpward ? spaceAbove : spaceBelow;
      if (usableSpace - chromeHeight < MIN_LIST_HEIGHT) {
        openUpward = spaceAbove > spaceBelow;
      }
      const nextUsableSpace = openUpward ? spaceAbove : spaceBelow;
      listMaxHeight = Math.max(
        MIN_LIST_HEIGHT,
        Math.min(PREFERRED_LIST_HEIGHT, nextUsableSpace - chromeHeight - viewportPadding),
      );
    }

    const nextPosition = {
      left,
      width,
      listMaxHeight,
      openUpward,
    };

    if (openUpward) {
      setPosition({
        ...nextPosition,
        top: null,
        bottom: window.innerHeight - rect.top + EDGE_GAP,
      });
      return;
    }

    setPosition({
      ...nextPosition,
      top: rect.bottom + EDGE_GAP,
      bottom: null,
    });
  }, [inlinePopover, showSearch, showSelectAll]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    updateFixedPosition();
    const frame = window.requestAnimationFrame(updateFixedPosition);
    const reposition = () => updateFixedPosition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open, updateFixedPosition]);

  const labelFor = (itemValue) =>
    normalizedOptions.find((option) => option.value === itemValue)?.label || itemValue;

  const filteredOptions = useMemo(() => {
    if (!showSearch || !search.trim()) return normalizedOptions;
    const term = search.trim().toLowerCase();
    return normalizedOptions.filter((option) => {
      const label = String(option.label || "").toLowerCase();
      const description = String(option.description || "").toLowerCase();
      return label.includes(term) || description.includes(term);
    });
  }, [normalizedOptions, search, showSearch]);

  const emitChange = (nextValues) => {
    if (disabled) return;
    onChange?.(nextValues);
  };

  const selectAllScopeOptions = useMemo(() => {
    if (!showSearch || !search.trim()) return normalizedOptions;
    return filteredOptions;
  }, [filteredOptions, normalizedOptions, search, showSearch]);

  const pendingSelectAllOptions = useMemo(
    () =>
      selectAllScopeOptions.filter(
        (option) => !selectedValues.includes(option.value),
      ),
    [selectAllScopeOptions, selectedValues],
  );

  const handleSelectAll = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled || pendingSelectAllOptions.length === 0) return;
    const next = [
      ...selectedValues,
      ...pendingSelectAllOptions.map((option) => option.value),
    ];
    emitChange(next);
  };

  const handleClear = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled || selectedValues.length === 0) return;
    if (showSearch && search.trim()) {
      const matching = new Set(
        selectAllScopeOptions.map((option) => option.value),
      );
      emitChange(selectedValues.filter((item) => !matching.has(item)));
      return;
    }
    emitChange([]);
  };

  const selectAllButtonLabel = useMemo(() => {
    if (showSearch && search.trim()) {
      return `${selectAllLabel} matching (${pendingSelectAllOptions.length})`;
    }
    return selectAllLabel;
  }, [pendingSelectAllOptions.length, search, selectAllLabel, showSearch]);

  const clearButtonLabel = useMemo(() => {
    if (showSearch && search.trim()) {
      const matchingSelected = selectedValues.filter((item) =>
        selectAllScopeOptions.some((option) => option.value === item),
      ).length;
      return matchingSelected
        ? `${clearLabel} matching (${matchingSelected})`
        : clearLabel;
    }
    return clearLabel;
  }, [
    clearLabel,
    search,
    selectAllScopeOptions,
    selectedValues,
    showSearch,
  ]);

  const toggleOption = (itemValue) => {
    if (disabled) return;
    const next = selectedValues.includes(itemValue)
      ? selectedValues.filter((valueItem) => valueItem !== itemValue)
      : [...selectedValues, itemValue];
    emitChange(next);
  };

  const togglePicker = (event) => {
    if (disabled) return;
    if (event?.target?.closest(".badge-multi-select__badge button")) return;
    const nextOpen = !open;
    if (nextOpen) {
      updateFixedPosition();
      window.requestAnimationFrame(updateFixedPosition);
    }
    setOpen(nextOpen);
  };

  const handleRootKeyDown = (event) => {
    if (disabled) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      togglePicker(event);
    }
  };

  const popoverStyle = position.openUpward
    ? {
        bottom: `${position.bottom}px`,
        left: `${position.left}px`,
        width: `${position.width}px`,
      }
    : {
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: `${position.width}px`,
      };

  const popover = open ? (
    <div
      className={`badge-multi-select__popover badge-multi-select__popover--anchored${inlinePopover ? " badge-multi-select__popover--compact" : ""}`}
      ref={popoverRef}
      style={popoverStyle}
    >
      {!inlinePopover ? (
        <div className="badge-multi-select__popover-head">
          <div>
            <strong>{popoverTitle}</strong>
            <span>{popoverSubtitle}</span>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label={`Close ${popoverTitle}`}
          >
            ×
          </button>
        </div>
      ) : null}
      {showSearch && (
        <div className="badge-multi-select__search-wrap">
          <input
            type="text"
            className="form-control form-control-sm badge-multi-select__search"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}
      {showSelectAll && normalizedOptions.length > 0 ? (
        <div className="badge-multi-select__bulk-actions">
          <button
            type="button"
            className="badge-multi-select__bulk-btn"
            disabled={disabled || pendingSelectAllOptions.length === 0}
            onClick={handleSelectAll}
          >
            {pendingSelectAllOptions.length === 0
              ? "All selected"
              : selectAllButtonLabel}
          </button>
          <button
            type="button"
            className="badge-multi-select__bulk-btn badge-multi-select__bulk-btn--clear"
            disabled={
              disabled ||
              selectedValues.length === 0 ||
              (Boolean(showSearch && search.trim()) &&
                !selectedValues.some((item) =>
                  selectAllScopeOptions.some((option) => option.value === item),
                ))
            }
            onClick={handleClear}
          >
            {clearButtonLabel}
          </button>
        </div>
      ) : null}
      <div
        className="badge-multi-select__popover-list"
        style={{ maxHeight: `${position.listMaxHeight}px` }}
      >
        {filteredOptions.length ? (
          filteredOptions.map((option) => {
            const selected = selectedValues.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                className={selected ? "is-selected" : ""}
                onClick={() => toggleOption(option.value)}
              >
                {typeof renderOption === "function" ? (
                  renderOption(option, { selected })
                ) : (
                  <span className="badge-multi-select__option-copy">
                    <span className="badge-multi-select__option-label">
                      {option.label}
                    </span>
                    {option.description ? (
                      <span className="badge-multi-select__option-description">
                        {option.description}
                      </span>
                    ) : null}
                  </span>
                )}
                {selected ? <small>Added</small> : null}
              </button>
            );
          })
        ) : (
          <div className="badge-multi-select__empty-option">No results</div>
        )}
      </div>
    </div>
  ) : null;

  return (
    <div
      ref={wrapRef}
      className={`badge-multi-select-wrap${inlinePopover ? " badge-multi-select-wrap--inline" : ""}${open ? " is-popover-open" : ""}`}
    >
      <div
        ref={rootRef}
        className={`badge-multi-select${disabled ? " is-disabled" : ""}${compactSummary ? " is-compact-summary" : ""}${open ? " is-open" : ""}`}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        tabIndex={disabled ? -1 : 0}
        onClick={togglePicker}
        onKeyDown={handleRootKeyDown}
      >
        {selectedValues.length ? (
          compactSummary && selectedValues.length > 1 ? (
            <span
              className="badge-multi-select__summary"
              title={selectedValues.map((itemValue) => labelFor(itemValue)).join(", ")}
            >
              {selectedValues.length} selected
            </span>
          ) : (
            selectedValues.map((itemValue) => (
              <span key={itemValue} className="badge-multi-select__badge">
                <span className="badge-multi-select__badge-label" title={labelFor(itemValue)}>
                  {labelFor(itemValue)}
                </span>
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleOption(itemValue);
                  }}
                  aria-label={`Remove ${labelFor(itemValue)}`}
                >
                  ×
                </button>
              </span>
            ))
          )
        ) : (
          <span className="badge-multi-select__empty">{emptyLabel}</span>
        )}
        <span className="badge-multi-select__add" aria-hidden="true">
          {addLabel}
        </span>
      </div>
      {popover ? ReactDOM.createPortal(popover, document.body) : null}
    </div>
  );
};

export default BadgeMultiSelect;
